import React, { useEffect, useRef, useCallback, useState } from "react";
import { DateTimeUtil, getNotificationStatusLabel } from "../../utils";
import { Loader2, User } from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { decrementUnreadNotifCount } from "../../redux/features/common/slice";
import { setSelectedCategoryForDetails } from "../../redux/features/notifications/slice";
import { useAppDispatch, RootState } from "../../redux/store";
import { MobileNotification } from "../../types";
import { getNotifications, getNotificationCategories, clearMobileNotifications, setSelectedNotification } from "../../redux/features/mobileNotifications";
import { getNotificationDetail } from "../../redux/features/notifications";

const NotificationsWeb: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isNotificationsLoading, notifications, notificationsMeta, isCategoriesLoading, categories } = useSelector(
        (state: RootState) => state.mobileNotifications
    );
    const user = useSelector((state: RootState) => state.user.user);

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    // Filter states
    const [selectedFilter, setSelectedFilter] = useState<"all" | "unread" | "read">("all");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

    const refreshData = useCallback(async () => {
        // Clear existing notifications before fetching new filtered results
        dispatch(clearMobileNotifications());
        
        // Build API params based on selected filters
        const params: any = { page: 1 };
        
        // Add notification_status if filter is not "all"
        if (selectedFilter === "unread") {
            params.notification_status = "0";
        } else if (selectedFilter === "read") {
            params.notification_status = "1";
        }
        
        // Add category if selected
        if (selectedCategory) {
            params.category = selectedCategory;
        }
        
        await dispatch(getNotifications(params));
    }, [dispatch, selectedFilter, selectedCategory]);

    useEffect(() => {
        // Check if categories are cached in localStorage
        const cachedCategories = localStorage.getItem('notificationCategories');
        if (!cachedCategories) {
            // Only fetch from server if not cached
            dispatch(getNotificationCategories());
        }
        
        refreshData();
    }, [dispatch]);

    // Reload notifications when filters change
    useEffect(() => {
        refreshData();
    }, [selectedFilter, selectedCategory]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (isCategoryDropdownOpen && !target.closest(".category-dropdown-container")) {
                setIsCategoryDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isCategoryDropdownOpen]);

    const loadMore = useCallback(() => {
        const loadedCount = notifications.length;
        const totalCount = notificationsMeta?.count ?? 0;
    
        // If we've loaded all notifications, do nothing
        if (loadedCount >= totalCount || isNotificationsLoading) return;
    
        const nextPage = (notificationsMeta?.page ?? 1) + 1;
        
        // Build API params with filters
        const params: any = { page: nextPage };
        
        if (selectedFilter === "unread") {
            params.notification_status = "0";
        } else if (selectedFilter === "read") {
            params.notification_status = "1";
        }
        
        if (selectedCategory) {
            params.category = selectedCategory;
        }
        
        dispatch(getNotifications(params));
    }, [dispatch, notificationsMeta?.page, notificationsMeta?.count, notifications.length, isNotificationsLoading, selectedFilter, selectedCategory]);    

    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container || isNotificationsLoading) return;

        const { scrollTop, scrollHeight, clientHeight } = container;

        // If near the bottom, trigger loading
        if (scrollTop + clientHeight >= scrollHeight - 100) {
            loadMore();
        }
    }, [isNotificationsLoading, loadMore]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [handleScroll]);

    const handleToDetails = (notification: MobileNotification) => {
        dispatch(setSelectedCategoryForDetails(notification.notification_category));
        dispatch(setSelectedNotification(notification));
        dispatch(decrementUnreadNotifCount());
        dispatch(getNotificationDetail({
            notificationId: notification.notification_id!,
            employeeId: Number(user.os_emp_account_id!),
        }));
        navigate("/notificationDetail");
    };

    return (
        <div className="md:w-1/2 mx-auto border-b bg-white h-full flex flex-col">
            <h2 className="text-2xl font-semibold text-gray-900 p-4 border-b">Notifications</h2>
            
            {/* Filter Section */}
            <div className="border-b border-gray-200 p-4 bg-gray-50">
                <div className="flex gap-2 items-center flex-wrap">
                    <button
                        onClick={() => setSelectedFilter("all")}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                            selectedFilter === "all"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setSelectedFilter("unread")}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                            selectedFilter === "unread"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                        Unread
                    </button>
                    <button
                        onClick={() => setSelectedFilter("read")}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                            selectedFilter === "read"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                        Read
                    </button>

                    {/* Category Button with Dropdown */}
                    <div className="relative category-dropdown-container inline-block">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
                            }}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
                                selectedCategory
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                            disabled={isCategoriesLoading}
                        >
                            <span>
                                {selectedCategory
                                    ? categories.find((c) => c.code === selectedCategory)?.name || "All Categories"
                                    : "All Categories"}
                            </span>
                            <svg
                                className={`w-4 h-4 transition-transform ${isCategoryDropdownOpen ? "rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {/* Dropdown Menu */}
                        {isCategoryDropdownOpen && (
                            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px] max-h-60 overflow-y-auto z-30">
                                <button
                                    onClick={() => {
                                        setSelectedCategory("");
                                        setIsCategoryDropdownOpen(false);
                                    }}
                                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                                        !selectedCategory ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-700"
                                    }`}
                                >
                                    All Categories
                                </button>
                                {categories.map((cat) => (
                                    <button
                                        key={cat.code}
                                        onClick={() => {
                                            setSelectedCategory(cat.code);
                                            setIsCategoryDropdownOpen(false);
                                        }}
                                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                                            selectedCategory === cat.code
                                                ? "bg-blue-50 text-blue-700 font-medium"
                                                : "text-gray-700"
                                        }`}
                                    >
                                        {cat.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {isNotificationsLoading && notifications.length === 0 ? (
                <div className="flex justify-center items-center flex-1">
                    <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
                </div>
            ) : notifications.length === 0 ? (
                <p className="text-gray-500">No notifications found.</p>
            ) : (
                <div
                    ref={scrollContainerRef}
                    className="overflow-y-auto flex-1 flex flex-col"
                >
                    {notifications.map((notification) => {
                        const mi = notification.message_info as any;
                        const title = mi?.title || mi?.TITLE || "Notification";
                        const body = mi?.body || mi?.MESSAGE || "You have a new notification.";
                        const bgColor = notification.notification_read_status === "0" ? "bg-gray-100" : "bg-white";
                        const currentStatus = notification.workflow_current_step_details?.current_status;
                        const statusInfo = getNotificationStatusLabel(currentStatus);

                        return (
                            <div
                                key={notification.notification_id}
                                className={`p-4 border-b flex items-start space-x-4 hover:bg-gray-100 transition cursor-pointer ${bgColor}`}
                                onClick={() => handleToDetails(notification)}
                            >
                                {notification.requestor?.image_url ? (
                                    <img
                                        src={notification.requestor.image_url}
                                        alt={notification.requestor.emp_name}
                                        className="w-12 h-12 rounded-full object-cover"
                                    />
                                ) : (
                                    <User className="w-12 h-12 text-gray-500" />
                                )}
                                <div className="flex-1">
                                    <h3 className="font-medium text-gray-900">{title}</h3>
                                    <p className="text-sm text-gray-700">{body}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {DateTimeUtil.FormatWithDayjsUtc(
                                            DateTimeUtil.convertUtcToLocalTime(notification!.notification_date_time!),
                                            DateTimeUtil.DATETIME_FANCY_2
                                        ) || "N/A"}{" "}
                                        | <span className={`${statusInfo.color}`}>{notification?.workflow_current_step_details?.status_text}</span>
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                    {isNotificationsLoading && (
                        <div className="flex justify-center items-center py-4">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-600" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationsWeb;
